import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar,
  Briefcase,
  DollarSign,
  Clock,
  CheckCircle2,
  ChevronRight,
  ArrowLeft,
  ExternalLink,
  Plus
} from 'lucide-react';
import apiClient from '../api/client';
import { toast } from 'sonner';
import ProjectModal from '../components/modals/ProjectModal';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currency';

const ClientDetails = () => {
  const { user } = useAuth();
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectModalOpen, setProjectModalOpen] = useState(false);

  const fetchClientData = React.useCallback(async () => {
    setLoading(true);
    try {
      const [clientRes, projectsRes, incomeRes] = await Promise.all([
        apiClient.get(`/clients/${clientId}`),
        apiClient.get(`/projects?client_id=${clientId}`),
        apiClient.get(`/income?client_id=${clientId}`)
      ]);

      setClient(clientRes.data);
      setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : projectsRes.data.items || []);
      setIncomeRecords(Array.isArray(incomeRes.data) ? incomeRes.data : incomeRes.data.items || []);
    } catch (err) {
      console.error('Failed to fetch client details', err);
      toast.error('Failed to load client details');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-slate-900">Client not found</h2>
        <Link to="/clients" className="text-indigo-600 hover:text-indigo-700 mt-4 inline-block font-medium">
          Back to Clients
        </Link>
      </div>
    );
  }

  const totalRevenue = incomeRecords
    .filter(r => r.payment_status === 'received')
    .reduce((sum, r) => sum + (r.amount || 0), 0);

  const pendingRevenue = incomeRecords
    .filter(r => r.payment_status === 'pending')
    .reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <div className="space-y-8 pb-12">
      {/* Header & Back Button */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Link
            to="/clients"
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{client.name}</h1>
            <p className="text-slate-500 font-medium flex items-center">
              <Building2 className="h-4 w-4 mr-1.5 text-slate-400" />
              {client.company || 'Private Participant'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setProjectModalOpen(true)}
            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all shadow-sm italic flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>New Project</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Client Info & Stats */}
        <div className="lg:col-span-1 space-y-6">
          {/* Contact Card */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 bg-slate-50/50">
              <h3 className="text-lg font-bold text-slate-900 italic">Contact Details</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                  <Mail className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Email Address</p>
                  <p className="text-sm font-medium text-slate-700">{client.email}</p>
                </div>
              </div>

              {client.phone && (
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Phone Number</p>
                    <p className="text-sm font-medium text-slate-700">{client.phone}</p>
                  </div>
                </div>
              )}

              {client.address && (
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                    <MapPin className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Address</p>
                    <p className="text-sm font-medium text-slate-700">{client.address}</p>
                  </div>
                </div>
              )}

              {client.website && (
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                    <Globe className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Website</p>
                    <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-indigo-600 hover:underline flex items-center">
                      {client.website.replace(/^https?:\/\//, '')}
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-indigo-600 rounded-3xl p-6 text-white shadow-xl shadow-indigo-100 relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex justify-between items-start mb-6">
                <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
                <div className="text-right">
                  <p className="text-indigo-100 text-xs font-bold uppercase tracking-widest italic">Total Lifetime Value</p>
                  <h3 className="text-3xl font-black italic">{getCurrencySymbol(user?.currency)}{totalRevenue.toLocaleString()}</h3>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm bg-white/10 p-3 rounded-xl backdrop-blur-sm">
                  <span className="text-indigo-100 font-medium">Pending Payments</span>
                  <span className="font-bold">{getCurrencySymbol(user?.currency)}{pendingRevenue.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-sm bg-white/10 p-3 rounded-xl backdrop-blur-sm">
                  <span className="text-indigo-100 font-medium">Active Projects</span>
                  <span className="font-bold">{projects.filter(p => p.status === 'ongoing').length}</span>
                </div>
              </div>
            </div>
            {/* Decorative circles */}
            <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
            <div className="absolute -left-4 -top-4 w-24 h-24 bg-indigo-400/20 rounded-full blur-xl"></div>
          </div>
        </div>

        {/* Right Column: Projects & History */}
        <div className="lg:col-span-2 space-y-8">
          {/* Projects List */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="text-lg font-bold text-slate-900 italic flex items-center">
                <Briefcase className="h-5 w-5 mr-2 text-indigo-500" />
                Associated Projects
              </h3>
              <span className="px-3 py-1 bg-white border border-slate-200 rounded-full text-xs font-bold text-slate-600 shadow-sm">
                {projects.length} Total
              </span>
            </div>
            <div className="divide-y divide-slate-50">
              {projects.length === 0 ? (
                <div className="p-10 text-center">
                  <p className="text-slate-400 italic font-medium">No projects associated with this client yet.</p>
                </div>
              ) : projects.map(project => (
                <Link
                  key={project.project_id}
                  to={`/projects/${project.project_id}`}
                  className="block p-6 hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex justify-between items-center">
                    <div className="space-y-1">
                      <h4 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors flex items-center">
                        {project.title}
                        <ChevronRight className="h-4 w-4 ml-1 opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" />
                      </h4>
                      <div className="flex items-center space-x-4 text-xs font-medium text-slate-500">
                        <span className="flex items-center">
                          <Calendar className="h-3.5 w-3.5 mr-1" />
                          {new Date(project.created_at).toLocaleDateString()}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tighter ${
                          project.status === 'ongoing' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                          project.status === 'completed' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                          'bg-slate-50 text-slate-600 border border-slate-100'
                        }`}>
                          {project.status}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-black text-slate-900 italic">{getCurrencySymbol(user?.currency)}{project.total_budget?.toLocaleString()}</p>
                      <div className="w-24 h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 rounded-full transition-all duration-1000"
                          style={{ width: `${project.progress_percentage || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Payment History */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="text-lg font-bold text-slate-900 italic flex items-center">
                <Clock className="h-5 w-5 mr-2 text-indigo-500" />
                Payment History
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50/50 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Date</th>
                    <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Details</th>
                    <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                    <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {incomeRecords.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-10 text-center text-slate-400 italic font-medium">
                        No payment records found for this client.
                      </td>
                    </tr>
                  ) : incomeRecords.map(record => (
                    <tr key={record.income_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-xs font-bold text-slate-600">
                        {new Date(record.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-bold text-slate-800 leading-tight">{record.notes || 'Project Payment'}</p>
                        <p className="text-[10px] text-slate-400 font-mono uppercase">{record.income_id}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase ${
                          record.payment_status === 'received' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                        }`}>
                          {record.payment_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-black text-slate-900 italic">{getCurrencySymbol(user?.currency)}{record.amount?.toLocaleString()}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <ProjectModal
        open={projectModalOpen}
        onClose={() => setProjectModalOpen(false)}
        onSuccess={fetchClientData}
        initialClientId={clientId}
      />
    </div>
  );
};

export default ClientDetails;
